import { generateId, isKeyboardClick } from "shared/functions";
import { EventListener, KeyCodes, type Event as MyonEvent } from "silver";

const KEY_DRAGGABLE = "__myonDraggable";
const DraggableRegistry: { [ draggableId: string ]: Draggable<any> } = { };

export interface DraggableEvent<T> extends MyonEvent {
	sender: Draggable<T>;
	keyboard: boolean;
}

export interface DropZoneEvent<T> extends MyonEvent {
	sender: DropZone<T>;
	draggable?: Draggable<any>;
	originalEvent: DragEvent;
}

export class Draggable<T> {

	public readonly onDragStart = EventListener.create<DraggableEvent<T>>("dragStart");
	public readonly onDragEnd = EventListener.create<DraggableEvent<T>>("dragEnd");
	public readonly onClick = EventListener.create<DraggableEvent<T> & { originalEvent: Event }>("click");

	public readonly element = document.createElement("button");

	private draggableId: string = generateId("draggable-");

	constructor(
		element: HTMLElement,
		dragImage: HTMLElement,
		readonly data: T
	) {
		(data as any)[ KEY_DRAGGABLE ] = this.draggableId;
		DraggableRegistry[ this.draggableId ] = this;
		element.setAttribute("draggable", "true");
		element.addEventListener("dragstart", (e: DragEventInit) => {
			let x = 0;
			let y = 0;
			const dataTransfer = e.dataTransfer as DataTransfer;
			if (e.clientX || e.clientY) {
				const boundingRect = element.getBoundingClientRect();
				x = (e.clientX as number) - boundingRect.left;
				y = (e.clientY as number) - boundingRect.top;
			}
			dataTransfer.setData("text", JSON.stringify(data));
			if (dataTransfer.setDragImage) {
				// IE11 does not support this
				dataTransfer.setDragImage(dragImage, x, y);
			}
			this.onDragStart.trigger({ sender: this, keyboard: false });
		});
		element.addEventListener("dragend", () => {
			this.onDragEnd.trigger({ sender: this, keyboard: false });
		});
		// Chrome does not understand "Enter" keypresses on <div> elements to be a click
		element.addEventListener("keypress", (e: KeyboardEvent) => {
			if (e.keyCode !== KeyCodes.ENTER && e.keyCode !== KeyCodes.SPACE) {
				return;
			}
			this.onClick.trigger({ sender: this, keyboard: true, originalEvent: e });
		});
		element.addEventListener("click", (e: MouseEvent) => {
			if (isKeyboardClick(e)) {
				return;
			}
			this.onClick.trigger({ sender: this, keyboard: false, originalEvent: e });
		});
	}
}

export class DropZone<T> {

	public readonly onDrop = EventListener.create<DropZoneEvent<T>>("drop");
	public readonly onDragOver = EventListener.create<DropZoneEvent<T>>("dragOver");
	public readonly onDragLeave = EventListener.create<DropZoneEvent<T>>("dragLeave");

	constructor(
		element: HTMLElement,
		public readonly data: T
	) {
		element.addEventListener("drop", (e: DragEvent) => {
			if (! e.dataTransfer) {
				return;
			}
			this.onDrop.trigger(this.parseEvent(e));
			e.preventDefault();
		});
		element.addEventListener("dragover", (e: DragEvent) => {
			if (!e.dataTransfer) {
				return;
			}
			this.onDragOver.trigger(this.parseEvent(e));
		});
		element.addEventListener("dragleave", (e: DragEvent) => {
			if (!e.dataTransfer) {
				return;
			}
			this.onDragLeave.trigger(this.parseEvent(e));
		});
	}

	private parseEvent(e: DragEvent): DropZoneEvent<T> {
		let draggable: Draggable<T>|undefined;
		try {
			const data =  (e.dataTransfer ? e.dataTransfer.getData("text") : "");
			const dataObj = JSON.parse(data);
			if (dataObj.hasOwnProperty(KEY_DRAGGABLE)) {
				draggable = DraggableRegistry[ dataObj[ KEY_DRAGGABLE ] ];
			}
		} catch (er) {
			console.warn(er);
		}
		return { sender: this, originalEvent: e, draggable };
	}
}
